import { type ICancellationRow } from '../types';

/**
 * The shell's cancellation alerts, as a module store for `useSyncExternalStore` (the
 * `incident-arrivals.ts` pattern: the feed runs in the shell, so a provider would have to wrap
 * the component that feeds it).
 *
 * Three rules make it safe under an election-day flood:
 *
 * 1. **Dedupe on the change, not the delivery.** SignalR redelivers on reconnect and the poll
 *    fallback sees the same row every 30 s. An alert is keyed by the row's *signature* —
 *    `id` + the fields a change can move — so the hub event and the next poll of the same
 *    change are one alert, and a hub `version` lower than one already held is ignored.
 * 2. **Seen survives a reload** — a localStorage set per (election, party). Reopening the tab
 *    does not re-ring 40 alerts the admin already acknowledged.
 * 3. **One tab rings.** Seen-marks are broadcast to sibling tabs, and the chime / desktop
 *    notification goes to whichever tab claims the change first (a visible tab is given a
 *    head start), so three open tabs do not make three sounds.
 */

export type AlertChangeKind = 'Recorded' | 'Withdrawn' | 'Reviewed' | 'Reinstated' | 'EvidenceAdded' | 'IncidentLinked' | 'Gone';

export interface ICancellationAlert {
	id: number;
	version: number | null;
	changeKind: AlertChangeKind | string;
	row: ICancellationRow;
	signature: string;
	receivedAt: number;
	/** False once the row has left the active ledger (withdrawn, reinstated, or gone from a poll). */
	active: boolean;
	unread: boolean;
}

export interface IAlertSnapshot {
	alerts: ICancellationAlert[];
	unread: number;
	/** The newest unread alert — the only one that gets `role="alert"`. */
	newestUnreadId: number | null;
}

/** Changes that deserve a sound. The rest update the card in place. */
export const ALERT_WORTHY = new Set<string>(['Recorded', 'Withdrawn']);

const MAX_ALERTS = 50;
const MAX_SEEN = 1000;
const STORAGE_PREFIX = 'pu-cancel-alerts:v1';
const CHIME_PREFIX = 'pu-cancel-chime:v1';
const CHANNEL_NAME = 'ielect-cancellation-alerts';
const CLAIM_TTL_MS = 30_000;

export const rowSignature = (row: ICancellationRow): string =>
	[row.id, row.state ?? 'Active', row.reviewStatus ?? '-', row.evidenceCount ?? '-', row.vesrIncidentId ?? '-'].join(':');

type Listener = () => void;

interface IStorageLike {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
}

interface IChannelLike {
	postMessage(message: unknown): void;
	close(): void;
	onmessage: ((event: { data: unknown }) => void) | null;
}

type ChannelMessage = { type: 'seen'; scope: string; signatures: string[] } | { type: 'chimed'; signature: string };

const safeStorage = (): IStorageLike | null => {
	try {
		return typeof window !== 'undefined' ? window.localStorage : null;
	} catch {
		return null;
	}
};

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

let storage: IStorageLike | null = safeStorage();
let channel: IChannelLike | null = null;
let scope = '';
let seen = new Set<string>();
let alerts = new Map<number, ICancellationAlert>();
const listeners = new Set<Listener>();
const chimed = new Set<string>();
let snapshot: IAlertSnapshot = { alerts: [], unread: 0, newestUnreadId: null };

const rebuild = () => {
	const list = [...alerts.values()].sort((a, b) => b.receivedAt - a.receivedAt || b.id - a.id);
	const unreadList = list.filter((a) => a.unread);
	snapshot = { alerts: list, unread: unreadList.length, newestUnreadId: unreadList[0]?.id ?? null };
	listeners.forEach((l) => l());
};

const persistSeen = () => {
	if (!storage || !scope) return;
	const trimmed = [...seen].slice(-MAX_SEEN);
	seen = new Set(trimmed);
	try {
		storage.setItem(`${STORAGE_PREFIX}:${scope}`, JSON.stringify(trimmed));
	} catch {
		// Private mode / quota — seen just won't survive a reload.
	}
};

const loadSeen = (key: string): Set<string> => {
	if (!storage) return new Set();
	try {
		const raw = storage.getItem(`${STORAGE_PREFIX}:${key}`);
		const parsed = raw ? (JSON.parse(raw) as unknown) : [];
		return new Set(Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : []);
	} catch {
		return new Set();
	}
};

const applySeen = (signatures: string[]) => {
	let changed = false;
	for (const sig of signatures) seen.add(sig);
	for (const alert of alerts.values()) {
		if (alert.unread && seen.has(alert.signature)) {
			alerts.set(alert.id, { ...alert, unread: false });
			changed = true;
		}
	}
	return changed;
};

type ChannelFactory = () => IChannelLike | null;
const defaultChannelFactory: ChannelFactory = () =>
	typeof BroadcastChannel === 'undefined' ? null : (new BroadcastChannel(CHANNEL_NAME) as unknown as IChannelLike);
let channelFactory: ChannelFactory = defaultChannelFactory;

const openChannel = () => {
	if (channel) return;
	try {
		channel = channelFactory();
		if (!channel) return;
		channel.onmessage = (event) => {
			const msg = event.data as ChannelMessage;
			if (msg?.type === 'seen' && msg.scope === scope) {
				if (applySeen(msg.signatures)) {
					persistSeen();
					rebuild();
				}
			} else if (msg?.type === 'chimed') {
				chimed.add(msg.signature);
			}
		};
	} catch {
		channel = null;
	}
};

const broadcast = (message: ChannelMessage) => {
	try {
		channel?.postMessage(message);
	} catch {
		// A closed channel must never break the alert itself.
	}
};

/**
 * First-writer-wins claim on "I will make the sound for this change". localStorage is shared
 * synchronously across a browser's tabs; the broadcast covers a tab that read before we wrote.
 */
export const claimChime = (signature: string, now = Date.now()): boolean => {
	if (chimed.has(signature)) return false;
	chimed.add(signature);
	if (!storage) return true;
	const key = `${CHIME_PREFIX}:${signature}`;
	try {
		const existing = storage.getItem(key);
		if (existing) {
			const [owner, at] = existing.split('|');
			if (owner !== tabId && now - Number(at) < CLAIM_TTL_MS) return false;
		}
		storage.setItem(key, `${tabId}|${now}`);
		const after = storage.getItem(key);
		if (after && !after.startsWith(`${tabId}|`)) return false;
	} catch {
		return true;
	}
	broadcast({ type: 'chimed', signature });
	return true;
};

export interface IReceiveInput {
	id: number;
	version?: number | null;
	changeKind: string;
	row: ICancellationRow;
	/**
	 * The signed-in user made this change themselves (recorded the unit, reinstated it…).
	 * Never rings and never creates an unread card; an existing card is updated and marked seen.
	 */
	own?: boolean;
}

export const cancellationAlerts = {
	subscribe: (listener: Listener) => {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	},

	/** Stable snapshot — useSyncExternalStore compares by reference. */
	getSnapshot: () => snapshot,

	/** Switch to an (election, party). Alerts belong to one scope; switching clears the stack. */
	setScope: (electionId: number, partyKey: number) => {
		const next = electionId > 0 && partyKey > 0 ? `${electionId}:${partyKey}` : '';
		if (next === scope) return;
		scope = next;
		seen = next ? loadSeen(next) : new Set();
		alerts = new Map();
		openChannel();
		rebuild();
	},

	getScope: () => scope,

	/** Mark rows as already known WITHOUT alerting — the first load after a page open. */
	seed: (rows: ICancellationRow[]) => {
		if (!scope) return;
		for (const row of rows) seen.add(rowSignature(row));
		persistSeen();
	},

	/**
	 * Take one change. Returns true when it produced a NEW unread alert worth a sound.
	 * An unseen non-alert-worthy change (evidence added, reviewed) updates the card silently.
	 */
	receive: ({ id, version = null, changeKind, row, own = false }: IReceiveInput, now = Date.now()): boolean => {
		if (!scope || !id) return false;
		const existing = alerts.get(id);
		const heldVersion = existing ? existing.version : null;
		if (heldVersion !== null && version !== null && version <= heldVersion) return false;
		const signature = rowSignature(row);
		const active = changeKind !== 'Gone' && (row.state === undefined || row.state === 'Active');
		if (existing?.signature === signature && existing.active === active) return false;

		const worthy = ALERT_WORTHY.has(changeKind);
		if (own) seen.add(signature);
		const alreadySeen = seen.has(signature);
		// A silent update to a card nobody has open yet stays silent; a card that was never
		// on the stack is only created for an alert-worthy change.
		if (!existing && (!worthy || alreadySeen)) {
			seen.add(signature);
			persistSeen();
			return false;
		}
		const unread = own ? false : worthy && !alreadySeen ? true : (existing?.unread ?? false);
		alerts.set(id, {
			id,
			version: version ?? existing?.version ?? null,
			changeKind,
			row,
			signature,
			receivedAt: worthy ? now : (existing?.receivedAt ?? now),
			active,
			unread,
		});
		if (alerts.size > MAX_ALERTS) {
			const oldest = [...alerts.values()].sort((a, b) => a.receivedAt - b.receivedAt)[0];
			if (oldest) alerts.delete(oldest.id);
		}
		rebuild();
		return worthy && !alreadySeen;
	},

	markSeen: (id: number) => {
		const alert = alerts.get(id);
		if (!alert?.unread) return;
		alerts.set(id, { ...alert, unread: false });
		seen.add(alert.signature);
		persistSeen();
		broadcast({ type: 'seen', scope, signatures: [alert.signature] });
		rebuild();
	},

	markAllSeen: () => {
		const signatures: string[] = [];
		for (const alert of alerts.values()) {
			if (!alert.unread) continue;
			alerts.set(alert.id, { ...alert, unread: false });
			seen.add(alert.signature);
			signatures.push(alert.signature);
		}
		if (signatures.length === 0) return;
		persistSeen();
		broadcast({ type: 'seen', scope, signatures });
		rebuild();
	},

	/** Remove from the stack (also marks it seen). */
	dismiss: (id: number) => {
		const alert = alerts.get(id);
		if (!alert) return;
		seen.add(alert.signature);
		alerts.delete(id);
		persistSeen();
		broadcast({ type: 'seen', scope, signatures: [alert.signature] });
		rebuild();
	},

	/** Test seams. */
	reset: () => {
		scope = '';
		seen = new Set();
		alerts = new Map();
		chimed.clear();
		listeners.clear();
		try {
			channel?.close();
		} catch {
			// ignore
		}
		channel = null;
		storage = safeStorage();
		snapshot = { alerts: [], unread: 0, newestUnreadId: null };
	},
	__setStorage: (next: IStorageLike | null) => {
		storage = next;
	},
	__setChannelFactory: (next: ChannelFactory | null) => {
		channelFactory = next ?? defaultChannelFactory;
	},
};
